import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { Button, Form, Input, Modal, Row, Tooltip } from "antd";
import { PlusCircleFilled } from "@ant-design/icons";
import moment from "moment";
import { useAppDispatch, useAppSelector } from "../../../../index";
import {
  closeEmailVersion,
  createContactEmail,
  getEmailVersions,
  getIndividualInfoById,
  updateEmailVersions,
} from "../../../../store/slices/accountsTs";
import { checkIsLoaded, checkIsLoading, useActiveModal } from "../../../../lib/utils";
import versionsTableUtils from "../../../../lib/utils/versionsTableUtils";
import {
  toDisplayFormat,
  toHttpFormat,
  toHttpFormatStartOfDay,
} from "../../../../lib/utils/formatDate";
import ToggleableVersionsTable from "../../../ToggleableVersionsTable/ToggleableVersionsTable";
import ChangeVersionsTable from "../../../common/ChangeVersionsTable";
import { Email, TableRecord } from "../../../../lib/interfaces";
import LocalStorageService from "../../../../services/LocalStorageService";

export const AccountContractsDetailsEmails = () => {
  const history = useHistory();
  const [emailId, setEmailId] = useState(null);
  const individualId = history.location.search.substring(1).split("=")[1];
  const {
    getIndividualByIdData,
    getIndividualByIdLoading,
    getEmailVersionsData,
    getEmailVersionsLoading,
  } = useAppSelector((state) => state.accountsTs);

  const [tableData, setTableData] = useState<any>([]);
  const [childTableData, setChildTableData] = useState<any>([]);
  const [form] = Form.useForm();
  const isChangingForbidden = versionsTableUtils.getIsTableChanging(childTableData);
  const dispatch = useAppDispatch();
  const [createForm] = Form.useForm();
  const { activeModal, setActiveModal } = useActiveModal("");

  const columnsWidths = {
    ...versionsTableUtils.columnsWidths,
    email: "65%",
    period: "20%",
    actions: "15%",
  };

  useEffect(() => {
    if (checkIsLoaded(getIndividualByIdLoading)) {
      if (getIndividualByIdData?.properties.length > 0) {
        setTableData(
          getIndividualByIdData?.emails?.map((item) => {
            return {
              key: item?.id,
              id: item?.id,
              fromDate: item?.fromDate,
              toDate: item?.toDate,
              email: item?.email,
            };
          })
        );
      }
    }
  }, [getIndividualByIdData]);

  useEffect(() => {
    if (checkIsLoaded(getEmailVersionsLoading)) {
      setChildTableData(
        getEmailVersionsData
          .map((item, i) => {
            return {
              id: item.id,
              fromDate: item.fromDate,
              toDate: item.toDate,
              email: item.email,
              ...versionsTableUtils.getCommonChildTableProps(item, i, getEmailVersionsData.length),
            };
          })
          .sort(versionsTableUtils.sortVersionsByDate)
      );
    }
  }, [getEmailVersionsData]);

  const [columns, setColumns] = useState([
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      width: columnsWidths.email,
      hidden: false,
    },
    {
      title: "Период действия",
      dataIndex: "period",
      key: "period",
      width: columnsWidths.period,
      hidden: false,
      render: (v, record) => {
        if (record.fromDate && record.toDate) {
          return `${toDisplayFormat(record.fromDate)} - ${toDisplayFormat(record.toDate)}`;
        }
        return "";
      },
    },
    {
      dataIndex: "actions",
      key: "actions",
      width: columnsWidths.actions,
      hidden: false,
    },
    {
      dataIndex: "buttons",
      key: "buttons",
      width: columnsWidths.buttons,
      render: () => (
        <div style={{ display: "flex", justifyContent: "end", alignItems: "center" }} />
      ),
    },
  ]);

  const getChildColumns = () => [
    {
      title: "Электронная почта",
      dataIndex: "email",
      key: "email",
      width: columnsWidths.email,
      hidden: false,
      render: (v, record) => (
        <ChangeVersionsTable.Input
          name="email"
          record={record}
          setFieldsValue={form.setFieldsValue}
          rules={[{ required: true, message: "", type: "email" }]}
        />
      ),
    },
    {
      title: "Период действия",
      dataIndex: "period",
      key: "period",
      width: columnsWidths.period,
      hidden: false,
      render: (text, record) => (
        <ChangeVersionsTable.Period
          record={record}
          setFieldsValue={form.setFieldsValue}
          fromDateRequired
          toDateRequired
          disabledToDate={(current) =>
            current.isBefore(record.fromDate, "day") || current.isAfter(record.toDate, "day")
          }
        />
      ),
    },
    {
      title: "",
      dataIndex: "actions",
      key: "actions",
      width: columnsWidths.actions,
      hidden: false,
      render: (value, record) => {
        //  const isHasAccess = hasAccess(MENU_ACCOUNT_ACCOUNTINFO_WRITE);
        return (
          <>
            <ChangeVersionsTable.AddButton
              // TODO пока захардкожены hasAccess=true
              hasAccess
              isChangingForbidden={isChangingForbidden}
              record={record}
              setChildTableData={setChildTableData}
            />
            <ChangeVersionsTable.EditButton
              hasAccess
              isChangingForbidden={isChangingForbidden}
              record={record}
              setChildTableData={setChildTableData}
            />
            <ChangeVersionsTable.CloseButton
              hasAccess
              isChangingForbidden={isChangingForbidden}
              record={record}
              setChildTableData={setChildTableData}
            />
            <ChangeVersionsTable.SubmitButton record={record} submit={form.submit} />
            <ChangeVersionsTable.CancelButton
              record={record}
              resetFields={form.resetFields}
              setChildTableData={setChildTableData}
            />
          </>
        );
      },
    },
    {
      dataIndex: "buttons",
      key: "buttons",
      width: columnsWidths.buttons,
      render: () => (
        <div style={{ display: "flex", justifyContent: "end", alignItems: "center" }} />
      ),
    },
  ];

  const [childColumns, setChildColumns] = useState(getChildColumns());

  useEffect(() => {
    setChildColumns(getChildColumns());
  }, [isChangingForbidden]);

  const renderCreateModal = () => {
    const closeCreateModal = () => {
      setActiveModal("");
      createForm.resetFields();
    };
    const onFinishCreate = (values) => {
      const tenantId = Number(LocalStorageService.getTenantId());
      const body: Email = {
        fromDate: toHttpFormatStartOfDay(moment()),
        email: values.email,
        dsc: "",
        tenantId,
      };
      const data = { individualId, body };
      dispatch(createContactEmail(data)).then(({ payload }) => {
        if (payload) {
          dispatch(getIndividualInfoById({ individualId }));
        }
      });
      closeCreateModal();
    };
    return (
      <Modal
        title="Электронная почта"
        okText="Сохранить"
        visible={activeModal === "accountDetailsCreateEmail"}
        onCancel={closeCreateModal}
        bodyStyle={{ paddingBottom: 0 }}
        footer={[
          <Button key="back" onClick={closeCreateModal}>
            Отмена
          </Button>,
          <Button key="submit" type="primary" onClick={createForm.submit}>
            Сохранить
          </Button>,
        ]}
      >
        <Form
          form={createForm}
          initialValues={{
            email: "",
          }}
          onFinish={onFinishCreate}
          labelCol={{ span: 6, offset: 4 }}
        >
          <Form.Item
            name="email"
            wrapperCol={{ span: 16, offset: 4 }}
            rules={[{ required: true, type: "email", message: "Введите корректный e-mail" }]}
          >
            <Input allowClear />
          </Form.Item>
        </Form>
      </Modal>
    );
  };

  const onFinish = (values) => {
    const version = childTableData.find(
      (item) => item.isCreated || item.isEditing || item.isClosing
    );
    const changeableRecord: TableRecord = versionsTableUtils.getChangeableRecord(childTableData);

    if (version.isCreated || version.isEditing) {
      const isForceUpdate = Boolean(changeableRecord.isEditing);
      const fromDate = isForceUpdate
        ? changeableRecord.fromDate
        : toHttpFormatStartOfDay(values.fromDate);

      dispatch(
        updateEmailVersions({
          emailId: changeableRecord?.id,
          fromDate,
          force: isForceUpdate,
          body: { email: values.email },
        })
      ).then(({ payload }) => {
        if (payload) {
          dispatch(getIndividualInfoById({ individualId }));
          dispatch(getEmailVersions(Number(emailId)));
        }
      });
    }

    if (version.isClosing) {
      dispatch(
        closeEmailVersion({
          emailId: changeableRecord?.id,
          fromDate: toHttpFormat(values.toDate),
        })
      ).then(() => {
        dispatch(getIndividualInfoById({ individualId }));
        dispatch(getEmailVersions(Number(emailId)));
      });
    }
  };
  return (
    <div className="account-details-table-wrapper">
      {renderCreateModal()}
      <Form form={form} onFinish={onFinish}>
        <ToggleableVersionsTable
          title={
            <Row align="middle">
              <h4 style={{ margin: 0 }}>Электронная почта</h4>
              <Tooltip title="Добавить электронную почту">
                <Button
                  style={{ color: "#00BB77", marginLeft: "15px", border: "none" }}
                  shape="circle"
                  icon={<PlusCircleFilled />}
                  onClick={() => setActiveModal("accountDetailsCreateEmail")}
                />
              </Tooltip>
            </Row>
          }
          tableName="account-details-table"
          columns={columns}
          setColumns={setColumns}
          tableData={tableData}
          childTableColumns={childColumns}
          setChildTableColumns={setChildColumns}
          childTableData={childTableData}
          isToggleableColumns={false}
          isChildTableLoading={checkIsLoading(getEmailVersionsLoading)}
          onOpenVersionsCallback={(record, expanded) => {
            if (!expanded) {
              dispatch(getEmailVersions(record?.id));
              setEmailId(record?.id);
            }
          }}
          isPageable={false}
        />
      </Form>
    </div>
  );
};
